import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// --- Progesterone Suppositories -------------------------------------

const progesteroneSuppositories: ContentBlock<"medication"> = {
  id: makeId("med-progesterone-suppositories"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "post-cycle",
    "during-cycle",
    "ivf",
    "iui",
    "fet",
    "evidence-based",
  ],
  title: "Endometrin",
  subtitle: "Progesterone vaginal suppositories for luteal phase support",
  body: {
    genericName: "Micronized progesterone (vaginal insert/suppository)",
    drugClass: "Progestogen (natural progesterone)",
    purpose:
      "Provides progesterone support in some ovulation, IVF, and FET protocols. Whether vaginal progesterone is appropriate, and how it is combined with monitoring or other treatment, depends on the person's protocol and clinic plan.",
    commonRoutes: ["vaginal"],
    commonForms: ["suppository"],
    dosageRanges:
      "There is no universal FET dose. The prescribed product, route, dose, timing, and duration come from the clinic's order and may differ by indication.",
    howToTake:
      "Use vaginal progesterone only as prescribed, using the product, route, dose, timing, and duration on your clinic's order or pharmacy label. Do not start, stop, or change treatment from this article. Ask your clinic or pharmacist how to use the specific product and what to do if a dose is late or missed; residue or irritation should be discussed with the care team when concerning.",
    sideEffects: [
      "Vaginal discharge or residue",
      "Vaginal irritation or itching",
      "Bloating",
      "Breast tenderness",
      "Drowsiness",
      "Headache",
      "Mood changes",
      "Constipation",
    ],
    storage:
      "Store at room temperature (up to 25°C / 77°F). Some products may require refrigeration - check specific packaging. Do not freeze.",
    brandsByCountry: [
      { country: "US", brands: ["Endometrin"] },
      { country: "UK", brands: ["Cyclogest", "Utrogestan", "Lubion"] },
      { country: "EU", brands: ["Utrogestan", "Cyclogest", "Lutinus"] },
      { country: "AU", brands: ["Utrogestan", "Cyclogest"] },
      {
        country: "FR",
        brands: ["Endometrin", "Utrogestan"],
        cisByBrand: {
          Utrogestan: "61673424",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (TGA, Medsafe, HPRA, Ethiopia
    // EFDA, MIMS Asia, Ferring/Besins).
    availability: [
      {
        name: "Endometrin",
        manufacturer: "Ferring",
        countries: ["US", "CA", "AU"],
      },
      {
        name: "Lutinus",
        manufacturer: "Ferring",
        countries: ["CA", "JP", "SA", "TR", "IE", "ET"],
      },
      {
        name: "Lutigest",
        manufacturer: "Ferring",
        countries: [
          "GB",
          "IE",
          "BG",
          "CZ",
          "DK",
          "FI",
          "DE",
          "GR",
          "HU",
          "IS",
          "NL",
          "NO",
          "PL",
          "SK",
          "ES",
          "SE",
        ],
      },
      {
        name: "Cyclogest",
        manufacturer: "Gedeon Richter",
        countries: ["GB", "AU", "HK", "SG", "MY", "NZ"],
      },
      {
        name: "Utrogestan",
        manufacturer: "Besins",
        countries: ["GB", "FR", "AU", "MY", "SG", "HK", "TH", "ID", "PH", "VN"],
      },
    ],
    aliases: [
      "Utrogestan",
      "Estima Gé",
      "Progestan",
      "Vaginal progesterone",
      "Progestérone vaginale",
    ],
    forWhom: "her",
    frBody: {
      genericName: "Progestérone micronisée (ovule/comprimé vaginal)",
      drugClass: "Progestatif (progestérone naturelle)",
      purpose:
        "Apporte un soutien progestatif dans certains protocoles d'ovulation, de FIV et de TEC. L'indication de la progestérone vaginale, son association au suivi et les autres traitements dépendent du protocole et du plan du centre.",
      dosageRanges:
        "Il n'existe pas de dose universelle pour un TEC. Le produit, la voie, la dose, le calendrier et la durée prescrits viennent de l'ordonnance du centre et peuvent varier selon l'indication.",
      howToTake:
        "Utilisez la progestérone vaginale uniquement selon l'ordonnance, avec le produit, la voie, la dose, le calendrier et la durée indiqués par votre centre ou la pharmacie. Ne commencez pas, n'arrêtez pas et ne modifiez pas le traitement à partir de cet article. Demandez au centre ou au pharmacien comment utiliser le produit précis et quoi faire en cas de dose retardée ou oubliée ; signalez à l'équipe tout résidu ou toute irritation préoccupante.",
      sideEffects: [
        "Pertes vaginales ou résidus",
        "Irritation ou démangeaisons vaginales",
        "Ballonnements",
        "Sensibilité des seins",
        "Somnolence",
        "Maux de tête",
        "Changements d'humeur",
        "Constipation",
      ],
      storage:
        "Conservez à température ambiante (jusqu'à 25 °C). Certains produits peuvent nécessiter une conservation au réfrigérateur - vérifiez l'emballage. Ne pas congeler.",
    },
  },
};

// --- Progesterone in Oil (PIO) --------------------------------------

const progesteroneInOil: ContentBlock<"medication"> = {
  id: makeId("med-progesterone-in-oil"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "post-cycle",
    "during-cycle",
    "ivf",
    "fet",
    "evidence-based",
  ],
  title: "Progesterone in Oil",
  subtitle: "Intramuscular progesterone injection for luteal support",
  body: {
    genericName: "Progesterone in oil (PIO)",
    drugClass: "Progestogen (natural progesterone in oil carrier)",
    purpose:
      "An intramuscular injection providing high and consistent progesterone levels for luteal phase support. Considered the gold standard for progesterone supplementation in IVF cycles due to reliable serum levels. Often preferred when vaginal progesterone alone does not achieve adequate serum progesterone concentrations, or as primary luteal support in many US clinics.",
    commonRoutes: ["intramuscular"],
    commonForms: ["vial"],
    dosageRanges:
      "There is no universal FET dose for progesterone in oil. The prescribed strength, route, injection schedule, and duration are set by the clinic from the order and product labeling.",
    howToTake:
      "Progesterone in oil should be used only after your clinic or pharmacist has given you product-specific administration instructions. Do not self-inject, change the dose or schedule, or substitute a product based on this article. Ask your care team what to do about a late or missed injection and report concerning injection-site or allergic symptoms promptly.",
    howToTakeByForm: {
      vial: "Follow the product label and the administration instructions provided by your clinic or pharmacist. This article cannot determine the dose, needle, injection site, timing, or duration for your plan. Do not change the product or injection schedule yourself; contact your care team about a late or missed dose or a concerning reaction.",
    },
    sideEffects: [
      "Injection site pain, lumps, and soreness",
      "Injection site bruising or sterile abscess (rare)",
      "Bloating",
      "Breast tenderness",
      "Drowsiness and fatigue",
      "Mood changes",
      "Constipation",
      "Allergic reaction to oil carrier (sesame or olive oil) - rare",
    ],
    storage:
      "Store at room temperature (20-25°C / 68-77°F). Do not refrigerate - cold oil is more viscous and harder to inject. Protect from light.",
    brandsByCountry: [
      { country: "US", brands: ["Progesterone in Oil (compounded)", "PIO"] },
      { country: "UK", brands: ["Prontogest", "Gestone"] },
      { country: "EU", brands: ["Prontogest", "Gestone"] },
      { country: "AU", brands: ["Gestone"] },
      { country: "FR", brands: ["Progestérone Retard Pharlon"] },
    ],
    // Availability deep-researched Jun 2026 (Ferring, IBSA, Bayer, India
    // pharmacies). In the US, IM progesterone in oil is usually compounded.
    availability: [
      { name: "Gestone", manufacturer: "Ferring", countries: ["GB", "IN"] },
      { name: "Prontogest", manufacturer: "IBSA", countries: ["IT"] },
      { name: "Proluton", manufacturer: "Bayer", countries: ["DE"] },
      { name: "Susten", manufacturer: "Sun Pharma", countries: ["IN"] },
      { name: "Naturogest", manufacturer: "Zydus", countries: ["IN"] },
    ],
    aliases: ["Gestone", "Progesterone in oil", "Progestérone huileuse", "PIO"],
    resources: [
      {
        label:
          "How to give an intramuscular progesterone in oil injection (CVS Specialty)",
        url: "https://www.youtube.com/watch?v=jr1Y5o7C6a4",
        type: "video",
      },
    ],
    forWhom: "her",
    frBody: {
      genericName: "Progestérone huileuse (PIO)",
      drugClass:
        "Progestatif (progestérone naturelle dans un excipient huileux)",
      purpose:
        "Injection intramusculaire apportant des taux de progestérone élevés et constants pour le soutien lutéal. Considérée comme la référence pour la supplémentation en progestérone en FIV grâce à des taux sériques fiables. Souvent privilégiée lorsque la progestérone vaginale seule ne permet pas d'atteindre des concentrations sériques suffisantes, ou comme soutien lutéal principal dans de nombreux centres.",
      dosageRanges:
        "Il n'existe pas de dose universelle de progestérone huileuse pour un TEC. Le dosage, la voie, le calendrier des injections et la durée sont fixés par le centre selon l'ordonnance et la notice du produit.",
      howToTake:
        "La progestérone huileuse doit être utilisée uniquement après les consignes d'administration propres au produit données par votre centre ou votre pharmacien. Ne vous injectez pas le produit, ne modifiez pas la dose ou le calendrier et ne remplacez pas un produit à partir de cet article. Demandez à votre équipe quoi faire en cas d'injection retardée ou oubliée et signalez rapidement toute réaction préoccupante au point d'injection ou tout signe allergique.",
      howToTakeByForm: {
        vial: "Suivez la notice et les consignes d'administration fournies par votre centre ou votre pharmacien. Cet article ne peut pas déterminer la dose, l'aiguille, le site d'injection, le calendrier ou la durée de votre traitement. Ne modifiez pas le produit ou le calendrier vous-même ; contactez votre équipe en cas de dose retardée ou oubliée ou de réaction préoccupante.",
      },
      sideEffects: [
        "Douleur, nodules et sensibilité au point d'injection",
        "Ecchymoses ou abcès stérile au point d'injection (rare)",
        "Ballonnements",
        "Sensibilité des seins",
        "Somnolence et fatigue",
        "Changements d'humeur",
        "Constipation",
        "Réaction allergique à l'excipient huileux (huile de sésame ou d'olive) - rare",
      ],
      storage:
        "Conservez à température ambiante (20-25 °C). Ne pas réfrigérer - une huile froide est plus visqueuse et plus difficile à injecter. À protéger de la lumière.",
    },
  },
};

// --- Progesterone Gel -----------------------------------------------

const progesteroneGel: ContentBlock<"medication"> = {
  id: makeId("med-progesterone-gel"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "post-cycle",
    "during-cycle",
    "ivf",
    "iui",
    "fet",
    "evidence-based",
  ],
  title: "Crinone",
  subtitle: "Progesterone vaginal gel for luteal phase support",
  body: {
    genericName: "Progesterone vaginal gel",
    drugClass: "Progestogen (natural progesterone gel)",
    purpose:
      "A vaginal gel formulation of progesterone for luteal phase support. The bioadhesive gel adheres to the vaginal wall, providing sustained local progesterone delivery to the uterus. Often preferred for its ease of use and less messy application compared to suppositories, though it may cause more gel residue buildup.",
    commonRoutes: ["vaginal"],
    commonForms: ["vial"],
    dosageRanges:
      "There is no universal FET dose for progesterone gel. The prescribed strength, frequency, route, and duration come from the clinic's order and product labeling.",
    howToTake:
      "Use the gel only as prescribed and follow the product label and your clinic's instructions for the applicator. Do not start, stop, repeat, or change the schedule based on this article. Ask your clinic or pharmacist what to do if a dose is late or missed; residue, irritation, or unusual symptoms should be discussed with the care team.",
    sideEffects: [
      "Vaginal discharge and gel residue accumulation",
      "Vaginal irritation",
      "Breast tenderness",
      "Headache",
      "Constipation",
      "Abdominal pain",
      "Drowsiness",
    ],
    storage:
      "Store at room temperature (15-25°C / 59-77°F). Do not refrigerate or freeze.",
    brandsByCountry: [
      { country: "US", brands: ["Crinone"] },
      { country: "UK", brands: ["Crinone"] },
      { country: "EU", brands: ["Crinone"] },
      { country: "AU", brands: ["Crinone"] },
      {
        country: "FR",
        brands: ["Crinone"],
        cisByBrand: {
          Crinone: "65015968",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (TGA, Health Canada, MIMS Asia,
    // India pharmacies). OneCrinone is the Japan brand.
    availability: [
      {
        name: "Crinone",
        manufacturer: "Merck Serono",
        countries: ["US", "GB", "CA", "AU", "ID", "SG", "IN"],
      },
      { name: "Prochieve", manufacturer: "Columbia", countries: ["US"] },
      { name: "OneCrinone", manufacturer: "Merck", countries: ["JP"] },
      { name: "Susten gel", manufacturer: "Sun Pharma", countries: ["IN"] },
      { name: "Naturogest gel", manufacturer: "Zydus", countries: ["IN"] },
      { name: "Dubagest gel", manufacturer: "Glenmark", countries: ["IN"] },
    ],
    aliases: ["Progesterone gel", "Crinone 8%"],
    forWhom: "her",
    frBody: {
      genericName: "Progestérone gel vaginal",
      drugClass: "Progestatif (gel de progestérone naturelle)",
      purpose:
        "Forme en gel vaginal de progestérone pour le soutien lutéal. Le gel bio-adhésif adhère à la paroi vaginale et permet une libération locale prolongée de progestérone vers l'utérus. Souvent privilégié pour sa facilité d'utilisation et son application moins salissante que les ovules, même si davantage de résidus de gel peuvent s'accumuler.",
      dosageRanges:
        "Il n'existe pas de dose universelle de gel de progestérone pour un TEC. Le dosage, la fréquence, la voie et la durée prescrits figurent sur l'ordonnance du centre et la notice du produit.",
      howToTake:
        "Utilisez le gel uniquement selon l'ordonnance et suivez la notice ainsi que les consignes de votre centre pour l'applicateur. Ne commencez pas, n'arrêtez pas, ne répétez pas et ne modifiez pas le calendrier à partir de cet article. Demandez au centre ou au pharmacien quoi faire en cas de dose retardée ou oubliée ; signalez à l'équipe tout résidu, toute irritation ou tout symptôme inhabituel.",
      sideEffects: [
        "Pertes vaginales et accumulation de résidus de gel",
        "Irritation vaginale",
        "Sensibilité des seins",
        "Maux de tête",
        "Constipation",
        "Douleurs abdominales",
        "Somnolence",
      ],
      storage:
        "Conservez à température ambiante (15-25 °C). Ne pas réfrigérer ni congeler.",
    },
  },
};

// --- Oral Progesterone ----------------------------------------------

const progesteroneOral: ContentBlock<"medication"> = {
  id: makeId("med-progesterone-oral"),
  kind: "medication",
  tags: ["medications", "her", "post-cycle", "ivf", "fet", "evidence-based"],
  title: "Prometrium",
  subtitle: "Oral micronized progesterone capsule",
  body: {
    genericName: "Micronized progesterone (oral)",
    drugClass: "Progestogen (natural progesterone)",
    purpose:
      "An oral capsule form of natural progesterone that may be used as supplementary luteal support or taken vaginally as an alternative to purpose-made suppositories. When taken orally, it undergoes first-pass liver metabolism which produces sedating metabolites. Some clinics prescribe it vaginally for more direct uterine absorption. Less commonly used as sole luteal support in IVF but may supplement other progesterone formulations.",
    commonRoutes: ["oral", "vaginal"],
    commonForms: ["capsule"],
    dosageRanges:
      "There is no universal FET dose for micronized progesterone. The prescribed route, strength, frequency, and duration come from the clinic's order and product labeling.",
    howToTake:
      "Use micronized progesterone only by the route and schedule prescribed by your clinic. Follow the product label; do not substitute oral and vaginal use, start or stop treatment, or change the dose based on this article. Oral use may cause drowsiness, so ask your clinic or pharmacist about driving and other safety precautions. Ask what to do if a dose is late or missed.",
    sideEffects: [
      "Significant drowsiness (oral route - take at bedtime)",
      "Dizziness",
      "Bloating",
      "Breast tenderness",
      "Headache",
      "Nausea (oral route)",
      "Mood changes",
      "Vaginal discharge (vaginal route)",
    ],
    storage:
      "Store at room temperature (up to 25°C / 77°F). Keep in the original container. Protect from light and moisture.",
    brandsByCountry: [
      { country: "US", brands: ["Prometrium"] },
      { country: "UK", brands: ["Utrogestan"] },
      { country: "EU", brands: ["Utrogestan", "Progestan"] },
      { country: "AU", brands: ["Utrogestan", "Prometrium"] },
      {
        country: "FR",
        brands: ["Utrogestan", "Estima", "Progestan"],
        cisByBrand: {
          Utrogestan: "61673424",
          Estima: "64018267",
          Progestan: "63393104",
        },
      },
    ],
    // Availability deep-researched Jun 2026 (Medsafe, MIMS Asia, India
    // pharmacies, Besins).
    availability: [
      {
        name: "Prometrium",
        manufacturer: "Besins / AbbVie",
        countries: ["US", "CA", "AU"],
      },
      {
        name: "Utrogestan",
        manufacturer: "Besins",
        countries: ["GB", "FR", "NZ", "MY", "SG", "HK", "TH", "ID", "PH", "VN"],
      },
      { name: "Microgest", manufacturer: "Cipla", countries: ["IN"] },
      { name: "Susten", manufacturer: "Sun Pharma", countries: ["IN"] },
      { name: "Naturogest", manufacturer: "Zydus", countries: ["IN"] },
      { name: "Dubagest", manufacturer: "Glenmark", countries: ["IN"] },
    ],
    aliases: ["Oral progesterone", "Progestérone orale"],
    forWhom: "her",
    frBody: {
      genericName: "Progestérone micronisée (orale)",
      drugClass: "Progestatif (progestérone naturelle)",
      purpose:
        "Forme orale en capsule de progestérone naturelle, utilisable comme soutien lutéal complémentaire ou administrée par voie vaginale comme alternative aux ovules dédiés. Par voie orale, elle subit un premier passage hépatique qui produit des métabolites sédatifs. Certains centres la prescrivent en intra-vaginale pour une absorption utérine plus directe. Moins souvent utilisée seule comme soutien lutéal en FIV, mais peut compléter d'autres formes de progestérone.",
      dosageRanges:
        "Il n'existe pas de dose universelle de progestérone micronisée pour un TEC. La voie, le dosage, la fréquence et la durée prescrits figurent sur l'ordonnance du centre et la notice du produit.",
      howToTake:
        "Utilisez la progestérone micronisée uniquement par la voie et selon le calendrier prescrits par votre centre. Suivez la notice ; ne remplacez pas la voie orale par la voie vaginale, ne commencez pas, n'arrêtez pas et ne modifiez pas le traitement à partir de cet article. La voie orale peut provoquer une somnolence : demandez au centre ou au pharmacien les précautions pour conduire et les autres mesures de sécurité. Demandez quoi faire en cas de dose retardée ou oubliée.",
      sideEffects: [
        "Somnolence marquée (voie orale - à prendre au coucher)",
        "Vertiges",
        "Ballonnements",
        "Sensibilité des seins",
        "Maux de tête",
        "Nausées (voie orale)",
        "Changements d'humeur",
        "Pertes vaginales (voie vaginale)",
      ],
      storage:
        "Conservez à température ambiante (jusqu'à 25 °C). Gardez dans l'emballage d'origine. À protéger de la lumière et de l'humidité.",
    },
  },
};

registerBlock(progesteroneSuppositories);
registerBlock(progesteroneInOil);
registerBlock(progesteroneGel);
registerBlock(progesteroneOral);

const dydrogesterone: ContentBlock<"medication"> = {
  id: makeId("med-dydrogesterone"),
  kind: "medication",
  tags: [
    "medications",
    "her",
    "during-cycle",
    "post-cycle",
    "ivf",
    "evidence-based",
  ],
  title: "Duphaston",
  subtitle: "Dydrogesterone - oral progestogen for luteal support",
  body: {
    genericName: "Dydrogesterone",
    drugClass: "Oral progestogen (synthetic progesterone)",
    purpose:
      "Dydrogesterone is an orally active progestogen that mimics natural progesterone. In IVF it is taken by mouth for luteal phase support, helping prepare and maintain the lining of the womb so an embryo can implant and a pregnancy can be sustained. It is also used to support pregnancy in threatened or recurrent miscarriage. The large LOTUS I and LOTUS II trials showed oral dydrogesterone was non-inferior to micronized vaginal progesterone for luteal support, offering a convenient tablet alternative.",
    commonRoutes: ["oral"],
    commonForms: ["pill"],
    dosageRanges:
      "There is no universal IVF or FET dose for dydrogesterone. The prescribed strength, frequency, indication, and duration come from the clinic's order and product labeling.",
    howToTake:
      "Use dydrogesterone only as prescribed and follow the product label. Do not start, stop, extend, or change the schedule based on this article. Ask your clinic or pharmacist what to do about a late or missed dose. Contact the care team promptly about unexpected bleeding, severe headache or migraine, sudden vision changes, leg pain or swelling, chest pain, or breathlessness.",
    sideEffects: [
      "Headache or migraine",
      "Nausea, vomiting, or abdominal pain",
      "Breast pain or tenderness",
      "Changes in menstrual or vaginal bleeding / spotting",
      "Mild drowsiness or dizziness, usually soon after a dose",
      "Less commonly: low mood, skin rash, or itching",
    ],
    storage:
      "Store in a dry place below 30°C (86°F) and not below 0°C (32°F), in the original pack. Keep out of the reach of children.",
    forWhom: "her",
    aliases: ["Dydrogesterone", "Duphaston"],
    availability: [
      {
        name: "Duphaston",
        manufacturer: "Abbott",
        countries: ["GB", "EU", "IN", "AE", "SG", "MY", "AU", "RU", "EG", "ZA"],
      },
    ],
    frBody: {
      genericName: "Dydrogestérone",
      drugClass: "Progestatif oral (progestérone de synthèse)",
      purpose:
        "La dydrogestérone est un progestatif actif par voie orale qui imite la progestérone naturelle. En FIV, elle se prend par la bouche pour le soutien de la phase lutéale : elle aide à préparer et maintenir la muqueuse de l'utérus afin qu'un embryon puisse s'implanter et que la grossesse se poursuive. Elle soutient aussi la grossesse en cas de menace de fausse couche ou de fausses couches à répétition. Les études LOTUS I et II ont montré qu'elle n'était pas inférieure à la progestérone vaginale micronisée pour le soutien lutéal.",
      dosageRanges:
        "Il n'existe pas de dose universelle de dydrogestérone pour la FIV ou un TEC. Le dosage, la fréquence, l'indication et la durée prescrits figurent sur l'ordonnance du centre et la notice du produit.",
      howToTake:
        "Utilisez la dydrogestérone uniquement selon l'ordonnance et la notice. Ne commencez pas, n'arrêtez pas, ne prolongez pas et ne modifiez pas le calendrier à partir de cet article. Demandez au centre ou au pharmacien quoi faire en cas de dose retardée ou oubliée. Contactez rapidement l'équipe en cas de saignement inhabituel, de maux de tête ou migraine sévères, de troubles soudains de la vision, de douleur ou gonflement d'une jambe, de douleur thoracique ou d'essoufflement.",
      sideEffects: [
        "Maux de tête ou migraine",
        "Nausées, vomissements ou douleurs au ventre",
        "Douleur ou sensibilité des seins",
        "Modifications des règles ou saignements vaginaux / pertes",
        "Légère somnolence ou vertiges, souvent peu après la prise",
        "Plus rarement : humeur basse, éruption cutanée ou démangeaisons",
      ],
      storage:
        "Conserver dans un endroit sec en dessous de 30°C (86°F) et pas en dessous de 0°C (32°F), dans l'emballage d'origine. Tenir hors de portée des enfants.",
    },
  },
};
registerBlock(dydrogesterone);

const progesteroneSubcutaneous: ContentBlock<"medication"> = {
  id: makeId("med-progesterone-subcutaneous"),
  kind: "medication",
  tags: ["medications", "her", "during-cycle", "post-cycle", "ivf"],
  title: "Lubion",
  subtitle:
    "Subcutaneous progesterone - water-soluble progesterone for luteal support",
  body: {
    genericName: "Progesterone (aqueous, subcutaneous)",
    drugClass: "Progestogen (natural progesterone for subcutaneous injection)",
    purpose:
      "Lubion is a water-soluble (aqueous) form of natural progesterone given as a small injection just under the skin. It provides luteal phase support during assisted reproduction (IVF/ICSI), maintaining the lining of the womb so an embryo can implant and an early pregnancy can be sustained. It is an alternative for women who cannot use or do not tolerate vaginal progesterone, and because it is water-based it is less painful and less likely to cause lumps than oily intramuscular progesterone.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["vial", "pre_filled_syringe"],
    dosageRanges:
      "There is no universal FET dose for subcutaneous progesterone. The prescribed strength, injection schedule, and duration come from the clinic's order and product labeling.",
    howToTake:
      "Subcutaneous progesterone should be used only after your clinic or pharmacist has given you product-specific administration instructions. Do not self-inject, change the dose or schedule, or substitute a product based on this article. Ask your care team what to do about a late or missed injection. Seek urgent advice for signs of a serious allergic reaction, severe abdominal symptoms, or heavy vaginal bleeding.",
    sideEffects: [
      "Injection-site reactions: redness, bruising, a firm lump, or tenderness",
      "Cramping of the womb (uterine spasm)",
      "Vaginal bleeding",
      "Headache",
      "Bloating, abdominal pain, nausea, vomiting, or constipation",
      "Breast tenderness or pain",
      "Tiredness",
    ],
    storage:
      "Store below 25°C (77°F). Do not refrigerate or freeze. Keep in the original pack to protect from light. Use immediately once opened and discard any unused solution.",
    forWhom: "her",
    aliases: [
      "Subcutaneous progesterone",
      "Aqueous progesterone",
      "Lubion",
      "Prolutex",
    ],
    availability: [
      {
        name: "Lubion",
        manufacturer: "IBSA",
        countries: ["GB", "EU", "IE"],
      },
      {
        name: "Prolutex",
        manufacturer: "IBSA",
        countries: ["EU"],
      },
    ],
    frBody: {
      genericName: "Progestérone (aqueuse, sous-cutanée)",
      drugClass:
        "Progestatif (progestérone naturelle pour injection sous-cutanée)",
      purpose:
        "Lubion est une forme hydrosoluble (aqueuse) de progestérone naturelle, administrée en petite injection juste sous la peau. Elle assure le soutien de la phase lutéale lors de la procréation médicalement assistée (FIV/ICSI), en maintenant la muqueuse de l'utérus pour permettre l'implantation de l'embryon. C'est une alternative pour les femmes qui ne tolèrent pas la progestérone vaginale ; étant à base d'eau, elle est moins douloureuse et provoque moins de nodules que les injections intramusculaires huileuses.",
      dosageRanges:
        "Il n'existe pas de dose universelle de progestérone sous-cutanée pour un TEC. Le dosage, le calendrier des injections et la durée prescrits figurent sur l'ordonnance du centre et la notice du produit.",
      howToTake:
        "La progestérone sous-cutanée doit être utilisée uniquement après les consignes d'administration propres au produit données par votre centre ou votre pharmacien. Ne vous injectez pas le produit, ne modifiez pas la dose ou le calendrier et ne remplacez pas un produit à partir de cet article. Demandez à votre équipe quoi faire en cas d'injection retardée ou oubliée. Demandez rapidement un avis en cas de signes de réaction allergique grave, de symptômes abdominaux sévères ou de saignements vaginaux abondants.",
      sideEffects: [
        "Réactions au point d'injection : rougeur, bleu, induration ou sensibilité",
        "Crampes de l'utérus (spasme utérin)",
        "Saignements vaginaux",
        "Maux de tête",
        "Ballonnements, douleurs au ventre, nausées, vomissements ou constipation",
        "Sensibilité ou douleur des seins",
        "Fatigue",
      ],
      storage:
        "Conserver en dessous de 25°C (77°F). Ne pas réfrigérer ni congeler. Conserver dans l'emballage d'origine à l'abri de la lumière. Utiliser immédiatement après ouverture et jeter toute solution non utilisée.",
    },
  },
};
registerBlock(progesteroneSubcutaneous);
